import axios from 'axios';
import {
  ScheduleWithDetails,
  CreateScheduleRequest,
  UpdateScheduleRequest,
  PublishSchedulesRequest,
  CheckConflictsRequest,
  CheckConflictsResponse,
} from '@/types/schedule';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

// Create axios instance with auth interceptor
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const scheduleService = {
  async getAllSchedules(filters?: {
    employee_id?: number;
    shift_type_id?: number;
    status?: 'draft' | 'published' | 'updated';
    start_date?: string;
    end_date?: string;
  }): Promise<ScheduleWithDetails[]> {    
    const token = localStorage.getItem('accessToken');
    
    const params = new URLSearchParams();
    if (filters?.employee_id) params.append('employee_id', filters.employee_id.toString());
    if (filters?.shift_type_id) params.append('shift_type_id', filters.shift_type_id.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);

    const url = `/schedules?${params.toString()}`;
    
    try {
      const response = await apiClient.get(url);
      return response.data.schedules;
    } catch (error: any) {
      throw error;
    }
  },

  async getScheduleById(id: number): Promise<ScheduleWithDetails> {
    const response = await apiClient.get(`/schedules/${id}`);
    return response.data.schedule;
  },

  async createSchedule(data: CreateScheduleRequest): Promise<ScheduleWithDetails> {
    try {
      const response = await apiClient.post('/schedules', data);
      return response.data.schedule;
    } catch (error: any) {
      throw error;
    }
  },

  async bulkCreateSchedules(data: {
    employee_id: number;
    shift_type_id: number;
    start_time: string;
    end_time: string;
    selected_dates: string[];
    status?: 'draft' | 'published' | 'updated';
  }): Promise<{
    message: string;
    summary: {
      total: number;
      created: number;
      conflicts: number;
      errors: number;
    };
    details: {
      created: any[];
      conflicts: any[];
      errors: any[];
    };
  }> {
    try {
      const response = await apiClient.post('/schedules/bulk', data);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  async updateSchedule(id: number, data: UpdateScheduleRequest): Promise<ScheduleWithDetails> {
    const response = await apiClient.put(`/schedules/${id}`, data);
    return response.data.schedule;
  },

  async deleteSchedule(id: number): Promise<void> {
    await apiClient.delete(`/schedules/${id}`);
  },

  async publishSchedules(data: PublishSchedulesRequest): Promise<void> {
    await apiClient.post('/schedules/publish', data);
  },

  async getEmployeeSchedules(
    employeeId: number,
    startDate?: string,
    endDate?: string
  ): Promise<ScheduleWithDetails[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await apiClient.get(`/schedules/employee/${employeeId}?${params.toString()}`);
    return response.data.schedules;
  },

  async getTeamSchedules(
    employeeIds: number[],
    startDate?: string,
    endDate?: string
  ): Promise<ScheduleWithDetails[]> {
    const params = new URLSearchParams();
    employeeIds.forEach(id => params.append('employeeIds', id.toString()));
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await apiClient.get(`/schedules/team/schedules?${params.toString()}`);
    return response.data.schedules;
  },

  async checkConflicts(data: CheckConflictsRequest): Promise<CheckConflictsResponse> {
    const response = await apiClient.post('/schedules/check-conflicts', data);
    return response.data;
  },

  async copySchedules(data: {
    source_start_date: string;
    source_end_date: string;
    target_start_date: string;
    employee_id?: number;
    shift_type_id?: number;
    status?: 'draft' | 'published' | 'updated';
  }): Promise<{
    message: string;
    copied: number;
    conflicts: number;
    details: {
      copied: ScheduleWithDetails[];
      conflicts: Array<{
        id: number;
        employee_id: number;
        start_datetime: string;
        end_datetime: string;
      }>;
    };
  }> {
    const response = await apiClient.post('/schedules/copy', data);
    return response.data;
  },
}; 